import { useState, FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import { WSLogo, WSHeartMark } from '../components/ui';

// Email/password sign-up. If Supabase has email confirmation enabled (it should
// for a real deployment), signUp returns no session and the user must confirm
// via the emailed link before signing in.
export function SignupScreen() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    setLoading(false);
    if (error) { setError(error.message); return; }
    // If confirmations are off, a session is returned and onAuthStateChange in
    // App will route straight into the portal. Otherwise, prompt to confirm.
    if (!data.session) setDone(true);
  }

  return (
    <div className="ws-fadein" style={{
      display: 'grid', gridTemplateColumns: '1fr 1.05fr',
      height: '100vh', width: '100%', background: 'var(--ws-bone)',
    }}>
      <div className="ws-scroll" style={{
        display: 'flex', flexDirection: 'column', padding: '40px 64px',
        position: 'relative', overflow: 'auto', background: 'var(--ws-paper)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <WSLogo size={28} />
          <a href="https://locate2u.com" style={{ fontSize: 12.5, color: 'var(--ws-mute)', textDecoration: 'none', letterSpacing: '0.04em' }}>
            locate2u.com →
          </a>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 420, margin: '0 auto', width: '100%' }}>
          <div style={{ marginBottom: 36 }}>
            <div className="ws-eyebrow-brand" style={{ marginBottom: 14 }}>Reporting Portal · Create account</div>
            {done ? (
              <>
                <h1 className="ws-display-thin" style={{ fontSize: 52, margin: '0 0 14px', color: 'var(--ws-ink)' }}>
                  Check your inbox.
                </h1>
                <div style={{ fontSize: 15, color: 'var(--ws-mute)', lineHeight: 1.55, maxWidth: 380 }}>
                  We sent a confirmation link to <strong style={{ color: 'var(--ws-ink-2)' }}>{email}</strong>. Click it to activate your account, then sign in.
                </div>
              </>
            ) : (
              <>
                <h1 className="ws-display-thin" style={{ fontSize: 52, margin: '0 0 14px', color: 'var(--ws-ink)' }}>
                  Get started.
                </h1>
                <div style={{ fontSize: 15, color: 'var(--ws-mute)', lineHeight: 1.55, maxWidth: 380 }}>
                  Create your account for the Locate2u delivery performance portal.
                </div>
              </>
            )}
          </div>

          {done ? (
            <button type="button" className="ws-btn ws-btn-primary" onClick={() => window.location.assign('/')} style={{ width: '100%', padding: '15px 22px' }}>
              Continue to sign in &nbsp;<span style={{ letterSpacing: 0, fontSize: 14 }}>→</span>
            </button>
          ) : (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
              <div>
                <label className="ws-field-label">Full name</label>
                <input type="text" required className="ws-input" value={fullName} onChange={e => setFullName(e.target.value)} autoComplete="name" placeholder="Jane Driver" />
              </div>
              <div>
                <label className="ws-field-label">Email address</label>
                <input type="email" required className="ws-input" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" placeholder="you@company.com" />
              </div>
              <div>
                <label className="ws-field-label">Password</label>
                <input type="password" required minLength={8} className="ws-input" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
              </div>
              <div>
                <label className="ws-field-label">Confirm password</label>
                <input type="password" required minLength={8} className="ws-input" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" />
              </div>

              {error && (
                <div style={{ fontSize: 13, color: 'var(--ws-danger)', background: 'rgba(200,50,45,0.06)', padding: '10px 14px', borderRadius: 4, border: '1px solid rgba(200,50,45,0.18)' }}>
                  {error}
                </div>
              )}

              <button type="submit" className="ws-btn ws-btn-primary" disabled={loading} style={{ width: '100%', padding: '15px 22px', marginTop: 4 }}>
                {loading ? (
                  <>
                    <svg className="ws-spin" width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="40" strokeDashoffset="20" />
                    </svg>
                    Creating account…
                  </>
                ) : (
                  <>Create account &nbsp;<span style={{ letterSpacing: 0, fontSize: 14 }}>→</span></>
                )}
              </button>

              <div style={{ fontSize: 13, color: 'var(--ws-mute)', textAlign: 'center' }}>
                Already have an account?{' '}
                <a href="/" onClick={(e) => { e.preventDefault(); window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }} style={{ color: 'var(--ws-blue)', textDecoration: 'none', fontWeight: 500 }}>
                  Sign in
                </a>
              </div>
            </form>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'var(--ws-mute-2)', letterSpacing: '0.04em', marginTop: 36 }}>
          <span>© Locate2u 2026 · Locate Technologies</span>
          <span style={{ display: 'flex', gap: 18 }}>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Status</a>
          </span>
        </div>
      </div>

      <div style={{
        position: 'relative', overflow: 'hidden',
        background: 'radial-gradient(120% 120% at 20% 0%, #1c4b82 0%, #12355B 45%, #0c2340 100%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 72px',
      }}>
        <WSHeartMark size={64} frontColor="#3B9EFF" backColor="#ffffff" />
        <div style={{ marginTop: 28, color: '#fff', fontSize: 40, fontWeight: 300, lineHeight: 1.15, letterSpacing: '-0.01em' }}>
          Delivery intelligence,<br />simplified.
        </div>
        <div style={{ marginTop: 18, color: 'rgba(255,255,255,0.7)', fontSize: 15, lineHeight: 1.6, maxWidth: 420 }}>
          Track performance, cost and customer experience across your entire delivery network — powered by Locate2u.
        </div>
      </div>
    </div>
  );
}
